import { FC, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
  activeStylesContinue,
  disabledStylesContinue,
  titleStyles,
} from "../../configs";
import { capitalizeFirstLetter, useIsMobile } from "../../utils";
import { handleAnalytics } from "../../utils/tracking";
import CustomCheckbox from "./components/CustomCheckbox";
import { hoverStylesContinue } from "../../configs/stylesLoader";
import { ArrowForward as ChevronRight } from "@mui/icons-material";
import { findBrandByDomain, getConfig } from "../../configs/configLoader";
import { resolvePrivacyPolicyUrl } from "../../utils/privacyPolicy";
import MeasuredBy from "../molecules/MeasuredBy";
import { useMediaQuery } from "react-responsive";
import GivenchyCheckbox from "./components/GivenchyCheckbox";

import "./index.css";

interface IPropsConsentScreen {
  step: {
    number: number;
  } | null;
  nextStep: () => void;
  clearMid: () => void;
  descriptionStyles: React.CSSProperties;
}

const ConsentScreen: FC<IPropsConsentScreen> = ({
  step,
  nextStep,
  clearMid,
  descriptionStyles,
}) => {
  const isMobile = useIsMobile();

  const isSmallHeightRelative = useMediaQuery({
    maxHeight: 767,
    minWidth: 767,
  });

  const { t } = useTranslation("components/consentement");

  const [checkedCookies, setCheckedCookies] = useState(false);
  const consentRef = useRef<HTMLDivElement>(null);

  const [hovered, setHovered] = useState(false);

  const brandDefined = findBrandByDomain();

  const isIntersport = brandDefined?.name === "Intersport";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";

  const isGivenchyMobile = isGivenchy && isMobile;
  const isGivenchyDesktop = isGivenchy && !isMobile;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedCookies(event.target.checked);

    if (!localStorage.getItem("checkedCookies")) {
      localStorage.setItem("checkedCookies", event.target.checked.toString());
    }

    handleAnalytics("action", "consent_click", {
      key: "value",
      value: event.target.checked,
    });
  };

  const brandConfig = getConfig();
  const privacyPolicyUrl = resolvePrivacyPolicyUrl(brandConfig?.privacyPolicy);

  const description = t("description", {
    ...(privacyPolicyUrl && { privacyPolicyUrl }),
  });
  const paragraphs = description.split("\n\n");

  const makeLinksClickable = (text: string) => {
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    return text.split(urlPattern).map((part: string, index: number) => {
      if (part.match(urlPattern)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="link-truncate"
            style={{
              color: isGivenchy ? "#000000" : "#1a0dab",
            }}
            title={part}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  useEffect(() => {
    if (consentRef.current) {
      consentRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const continueButtonStyles = !checkedCookies
    ? disabledStylesContinue
    : activeStylesContinue;

  const handleKeyDown = (e: any) => {
    e.stopPropagation();

    if (e.key === "Enter") {
      if (!checkedCookies) setCheckedCookies(true);
      if (checkedCookies) nextStep();
    }
  };

  const CTAStyles: React.CSSProperties = {
    ...continueButtonStyles,
    ...(hovered && checkedCookies ? hoverStylesContinue : {}),
    textTransform:
      continueButtonStyles.textTransform === "capitalize"
        ? "none"
        : continueButtonStyles.textTransform,
    marginTop: isGivenchyDesktop ? 0 : isGivenchyMobile ? "30px" : "20px",
    padding: "16px",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "48px",
    marginBottom: isGivenchyDesktop && isSmallHeightRelative ? "80px" : "0",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered && checkedCookies ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered && checkedCookies ? 1 : 0,
    transform: hovered && checkedCookies ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  return (
    <div
      className="consent"
      ref={consentRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        className="consent-description"
        style={{
          margin: isGivenchy ? "40px 0" : 0,
        }}
      >
        {paragraphs.map((paragraph, index) => (
          <p
            key={index}
            style={{
              ...descriptionStyles,
              color: isGivenchy ? "#000000" : "",
              marginTop: isGivenchy ? "0" : "10px",
              marginBottom: isGivenchy ? "20px" : "15px",
              lineHeight: isGivenchy ? "20px" : "",
              letterSpacing: (titleStyles as any)?.letterSpacing,
            }}
          >
            {makeLinksClickable(paragraph)}
          </p>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          position:
            isGivenchyDesktop && !isSmallHeightRelative ? "fixed" : "relative",
          bottom: isGivenchyDesktop && !isSmallHeightRelative ? "155px" : 0,
          width:
            isGivenchyDesktop && !isSmallHeightRelative
              ? "calc(100% - 140px)"
              : "auto",
        }}
      >
        <div
          className="check-container"
          style={{
            display: "flex",
            alignItems: isGivenchy ? "baseline" : "center",
          }}
        >
          {isGivenchy ? (
            <GivenchyCheckbox
              checked={checkedCookies}
              onChange={handleChange}
              aria-labelledby="consent-access-text"
              sx={{
                color: "#000000",
                position: "relative",
                bottom: "5px",
              }}
            />
          ) : (
            <CustomCheckbox
              checked={checkedCookies}
              onChange={handleChange}
              aria-labelledby="consent-access-text"
              style={{
                color: "#000000",
                margin: 0,
                padding: 0,
              }}
            />
          )}
          <p
            id="consent-access-text"
            style={{
              fontSize: descriptionStyles.fontSize,
              color: isGivenchy ? "#000000" : descriptionStyles.color,
              textAlign:
                titleStyles.textAlign as React.CSSProperties["textAlign"],
              justifyContent:
                titleStyles.textAlign as React.CSSProperties["textAlign"],
              display: "flex",
              alignItems: isGivenchy ? "baseline" : "left",
              lineHeight: isGivenchy ? "20px" : "",
              letterSpacing: (titleStyles as any)?.letterSpacing,
              position: "relative",
              bottom: isGivenchy ? "10px" : "0",
            }}
          >
            {t("access")}
          </p>
        </div>
        <button
          tabIndex={0}
          type="button"
          className={`continue-button breasts-continue`}
          onMouseEnter={(e) => {
            if (isMobile) return;
            setHovered(true);
            if (checkedCookies) {
              Object.assign(e.currentTarget.style, hoverStylesContinue);
            }
          }}
          onMouseLeave={(e) => {
            setHovered(false);
            if (checkedCookies) {
              Object.assign(e.currentTarget.style, continueButtonStyles);
            }
          }}
          disabled={!checkedCookies}
          onClick={() => nextStep()}
          style={CTAStyles}
        >
          <div style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
            {continueButtonStyles.textTransform === "capitalize"
              ? capitalizeFirstLetter(t("submit"))
              : t("submit")}

            {isIntersport && (
              <span style={arrowContainerStyle} aria-hidden>
                <ChevronRight style={iconStyle} />
              </span>
            )}
          </div>
        </button>
      </div>
      {isGivenchy ? <MeasuredBy step={step} /> : null}
    </div>
  );
};

export default ConsentScreen;
